import { Link, useParams } from 'react-router-dom';
import type { DiscoveredKit, KitContext } from '../shared/discoverKits';

interface KitDetailProps {
    kits: DiscoveredKit[];
}

const CONTEXT_LABELS: Record<KitContext, string> = {
    size2mix: 'Size2Mix',
    urbanmap: 'Urban Map',
};

// Eagerly load all screenshot URLs so we can resolve view.screenshot file
// names to web-served URLs (Vite-handled). Returns `{ [absolutePath]: url }`.
const screenshotUrls = import.meta.glob('../kits/*/screenshots/*.{png,jpg,jpeg,webp}', {
    eager: true,
    query: '?url',
    import: 'default',
}) as Record<string, string>;

function findScreenshotUrl(slug: string, fileName?: string): string | undefined {
    if (!fileName) return undefined;
    const suffix = `/kits/${slug}/screenshots/${fileName}`;
    const found = Object.entries(screenshotUrls).find(([k]) => k.endsWith(suffix));
    return found?.[1];
}

export default function KitDetail({ kits }: KitDetailProps) {
    const { slug } = useParams<{ slug: string }>();
    const kit = kits.find((k) => k.slug === slug);

    if (!kit) {
        return (
            <div style={{ padding: 40, fontFamily: 'var(--nj-core-font-family-sans)' }}>
                <p>Kit introuvable.</p>
                <Link to="/kits">← Tous les kits</Link>
            </div>
        );
    }

    const { manifest } = kit;

    return (
        <div
            style={{
                minHeight: '100vh',
                background:
                    'var(--nj-semantic-color-background-neutral-secondary-default)',
                fontFamily: 'var(--nj-core-font-family-sans)',
            }}
        >
            <header
                style={{
                    height: 56,
                    background:
                        'var(--nj-semantic-color-background-neutral-primary-default)',
                    borderBottom: '1px solid var(--nj-core-color-grey-300)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    padding: '0 24px',
                }}
            >
                <Link
                    to="/kits"
                    style={{
                        fontSize: 13,
                        color: 'var(--nj-semantic-color-text-secondary-default)',
                        textDecoration: 'none',
                    }}
                >
                    ← Kits
                </Link>
                <h1 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                    {manifest.name}
                </h1>
                <span
                    style={{
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '3px 8px',
                        borderRadius: 4,
                        background: 'var(--nj-core-color-grey-100)',
                        color: 'var(--nj-semantic-color-text-secondary-default)',
                    }}
                >
                    {CONTEXT_LABELS[manifest.context]}
                </span>
            </header>

            <main
                style={{
                    maxWidth: 1024,
                    margin: '32px auto',
                    padding: '0 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 24,
                }}
            >
                <p style={{ margin: 0, fontSize: 14 }}>{manifest.description}</p>

                <Section title="Sources Nemo réelles">
                    <ul style={{ paddingLeft: 18, margin: 0 }}>
                        {manifest.nemoSources.map((s) => (
                            <li key={s.path} style={{ marginBottom: 6, fontSize: 13 }}>
                                <strong>{s.label}</strong> —{' '}
                                <code style={{ fontSize: 12 }}>{s.path}</code>
                                {s.note && (
                                    <div
                                        style={{
                                            fontSize: 12,
                                            color: 'var(--nj-semantic-color-text-secondary-default)',
                                            marginTop: 2,
                                        }}
                                    >
                                        {s.note}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </Section>

                {manifest.sharedComponents && manifest.sharedComponents.length > 0 && (
                    <Section title="Composants partagés à utiliser">
                        <ul style={{ paddingLeft: 18, margin: 0 }}>
                            {manifest.sharedComponents.map((c) => (
                                <li key={c} style={{ fontSize: 13 }}>
                                    <code>{c}</code>
                                </li>
                            ))}
                        </ul>
                    </Section>
                )}

                <Section title={`Vues (${manifest.views.length})`}>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                            gap: 16,
                        }}
                    >
                        {manifest.views.map((view) => {
                            const url = findScreenshotUrl(kit.slug, view.screenshot);
                            return (
                                <div
                                    key={view.id}
                                    style={{
                                        background: 'white',
                                        border: '1px solid var(--nj-core-color-grey-200)',
                                        borderRadius: 8,
                                        overflow: 'hidden',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <div
                                        style={{
                                            background:
                                                'var(--nj-core-color-grey-100)',
                                            aspectRatio: '16/10',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'var(--nj-semantic-color-text-secondary-default)',
                                            fontSize: 12,
                                        }}
                                    >
                                        {url ? (
                                            <img
                                                src={url}
                                                alt={view.label}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                }}
                                            />
                                        ) : (
                                            <span>📷 capture à venir</span>
                                        )}
                                    </div>
                                    <div style={{ padding: '12px 14px' }}>
                                        <div
                                            style={{
                                                fontSize: 13,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {view.label}
                                        </div>
                                        <div
                                            style={{
                                                fontSize: 12,
                                                color: 'var(--nj-semantic-color-text-secondary-default)',
                                                marginTop: 4,
                                            }}
                                        >
                                            {view.description}
                                        </div>
                                        {view.prototypeSlug && (
                                            <Link
                                                to={`/prototype/${view.prototypeSlug}`}
                                                style={{
                                                    display: 'inline-block',
                                                    marginTop: 8,
                                                    fontSize: 12,
                                                    color: 'var(--nj-semantic-color-text-action-default)',
                                                }}
                                            >
                                                Voir le prototype →
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Section>

                {manifest.notes && (
                    <Section title="Notes">
                        <pre
                            style={{
                                fontSize: 12,
                                background: 'white',
                                padding: 12,
                                borderRadius: 6,
                                border: '1px solid var(--nj-core-color-grey-200)',
                                whiteSpace: 'pre-wrap',
                                margin: 0,
                                fontFamily: 'inherit',
                            }}
                        >
                            {manifest.notes}
                        </pre>
                    </Section>
                )}
            </main>
        </div>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <section>
            <h2
                style={{
                    fontSize: 12,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: 'var(--nj-semantic-color-text-secondary-default)',
                    margin: '0 0 8px',
                }}
            >
                {title}
            </h2>
            {children}
        </section>
    );
}
